import { Enemy } from '../entities/Enemy';
import { Boss } from '../entities/Boss';
import { getWaveConfig } from '../config/waves';
import { getLevelDefinition, type WaveDefinition } from '../config/levelWaves';

export interface WaveState {
  region: number;
  wave: number;
  active: boolean;
  complete: boolean;
  isBoss: boolean;
  isMiniBoss: boolean;
}

interface SpawnGroup {
  enemyType: string;
  remainingCount: number;
  nextSpawnTime: number;
  spawnDelay: number;
  formationSize: number; // Spawn enemies in groups
  formationTimer: number; // Time between enemies in a formation
}

export class WaveManager {
  private currentRegion: number = 1;
  private currentWave: number = 1;
  private waveActive: boolean = false;
  private waveComplete: boolean = false;
  private waveTimer: number = 0;
  private spawnTimer: number = 0;
  private enemies: Enemy[] = [];
  private boss: Boss | null = null;
  
  // New wave system
  private currentLevelDef: any = null;
  private currentWaveDef: WaveDefinition | null = null;
  private spawnGroups: SpawnGroup[] = [];
  private waveDuration: number = 0;
  private useNewWaveSystem: boolean = false;
  private enemiesKilledThisWave: number = 0;
  private powerUpDropped: boolean = false;
  
  // Original game spawn mechanics (fallback)
  private baseSpawnRate: number = 2000; // 2 seconds in ms
  private currentSpawnDelay: number = 2000;
  private enemiesSpawned: number = 0;
  private enemiesKilled: number = 0;
  private maxEnemiesPerWave: number = 20; // Limit enemies per wave

  public startWave(region: number, wave: number): void {
    this.currentRegion = region;
    this.currentWave = wave;
    this.waveActive = true;
    this.waveComplete = false;
    this.waveTimer = 0;
    this.spawnTimer = 0;
    this.enemies = [];
    this.boss = null;
    this.enemiesKilledThisWave = 0;
    this.powerUpDropped = false;
    
    // Try to use new wave system first
    const levelDef = getLevelDefinition(region);
    if (levelDef && wave <= levelDef.waves.length) {
      // Use new wave system
      this.useNewWaveSystem = true;
      this.currentLevelDef = levelDef;
      this.currentWaveDef = levelDef.waves[wave - 1];
      this.waveDuration = this.currentWaveDef.duration;
      this.initializeSpawnGroups();
      console.log(`Starting wave ${wave} (NEW SYSTEM) - ${this.currentWaveDef.purpose}`);
    } else {
      // Fallback to original wave system
      this.useNewWaveSystem = false;
      this.enemiesSpawned = 0;
      this.enemiesKilled = 0;
      this.currentSpawnDelay = Math.max(800, this.baseSpawnRate - (wave * 100));
      this.maxEnemiesPerWave = 15 + (wave * 3);

      const waveConfig = getWaveConfig(region, wave);
      if (!waveConfig) {
        console.error(`Wave config not found for region ${region}, wave ${wave}`);
        return;
      }

      if (waveConfig.isBoss || waveConfig.isMiniBoss) {
        this.spawnBoss(waveConfig.bossType || 'giant_hunger_worm');
      } else {
        console.log(`Starting wave ${wave} (OLD SYSTEM) with spawn delay ${this.currentSpawnDelay}ms`);
      }
    }
  }

  private initializeSpawnGroups(): void {
    if (!this.currentWaveDef) return;
    
    this.spawnGroups = [];
    for (const spawn of this.currentWaveDef.spawns) {
      // Determine formation size based on spawn delay
      // Faster spawns = larger formations
      const formationSize = spawn.spawnDelay < 1 ? 4 : spawn.spawnDelay < 1.5 ? 3 : 2;
      
      this.spawnGroups.push({
        enemyType: spawn.enemyType,
        remainingCount: spawn.count,
        nextSpawnTime: 0, // First spawn happens immediately
        spawnDelay: spawn.spawnDelay,
        formationSize,
        formationTimer: 0.15 // 150ms between enemies in a formation
      });
    }
  }

  private spawnBoss(bossType: string): void {
    this.boss = new Boss(240, 100, bossType);
    console.log(`Boss spawned: ${bossType}`);
  }

  public update(deltaTime: number): void {
    if (!this.waveActive) return;

    this.waveTimer += deltaTime;
    this.spawnTimer += deltaTime;

    if (this.useNewWaveSystem) {
      this.updateNewWaveSystem(deltaTime);
    } else {
      this.updateOldWaveSystem(deltaTime);
    }

    // Update all enemies
    for (const enemy of this.enemies) {
      if (enemy.active) {
        enemy.update(deltaTime);
      }
    }

    // Update boss
    if (this.boss && this.boss.active) {
      this.boss.update(deltaTime);
    }

    // Check if wave is complete
    this.checkWaveComplete();
  }

  private updateNewWaveSystem(deltaTime: number): void {
    // Spawn enemies from each group based on their timers
    for (const group of this.spawnGroups) {
      if (group.remainingCount > 0) {
        group.nextSpawnTime -= deltaTime;
        
        if (group.nextSpawnTime <= 0) {
          // Spawn formation of enemies
          const toSpawn = Math.min(group.formationSize, group.remainingCount);
          this.spawnFormation(group.enemyType, toSpawn, group.formationTimer);
          group.remainingCount -= toSpawn;
          group.nextSpawnTime = group.spawnDelay;
        }
      }
    }

    // Check for power-up drop
    if (!this.powerUpDropped && this.currentWaveDef?.powerUpDrop) {
      const activeCount = this.enemies.filter(e => e.active).length;
      const totalSpawned = this.getTotalEnemiesSpawned();
      this.enemiesKilledThisWave = totalSpawned - activeCount;
      
      // Drop power-up when enough enemies killed
      if (this.enemiesKilledThisWave >= this.currentWaveDef.powerUpDrop.afterEnemyCount) {
        this.powerUpDropped = true;
        // Power-up dropping will be handled by PlayState
        console.log(`Power-up ready: ${this.currentWaveDef.powerUpDrop.type}`);
      }
    }
  }

  private updateOldWaveSystem(deltaTime: number): void {
    // Continuously spawn enemies based on spawn delay (like original game)
    if (!this.boss && this.spawnTimer >= this.currentSpawnDelay / 1000 && this.enemiesSpawned < this.maxEnemiesPerWave) {
      this.spawnEnemies();
      this.spawnTimer = 0;
    }
    
    // Count killed enemies
    const activeCount = this.enemies.filter(e => e.active).length;
    this.enemiesKilled = this.enemiesSpawned - activeCount;
  }

  private spawnEnemyOfType(type: string): void {
    // Randomized spawn positions
    let finalX, finalY;
    const spawnSide = Math.random();
    
    if (spawnSide < 0.7) {
      // Top spawn (most common - 70%)
      finalX = Math.random() * 450 + 15;
      finalY = -30;
    } else if (spawnSide < 0.85) {
      // Left side spawn (15%)
      finalX = -30;
      finalY = Math.random() * 200;
    } else {
      // Right side spawn (15%)
      finalX = 480 + 30;
      finalY = Math.random() * 200;
    }
    
    const enemy = new Enemy(finalX, finalY, type);
    this.enemies.push(enemy);
  }

  private spawnFormation(type: string, count: number, formationDelay: number): void {
    const formation = this.getFormationPattern(count);
    
    // Spawn all enemies in formation at once
    for (let i = 0; i < count; i++) {
      const pos = formation[i % formation.length];
      const enemy = new Enemy(pos.x, pos.y, type);
      this.enemies.push(enemy);
    }
  }

  private getFormationPattern(count: number): Array<{x: number, y: number}> {
    const patterns = [
      // V-Formation (top center)
      () => {
        const positions = [];
        const centerX = 240;
        const startY = -30;
        for (let i = 0; i < count; i++) {
          const offset = Math.floor(i / 2) * 40;
          const x = i % 2 === 0 ? centerX + offset : centerX - offset;
          positions.push({ x: Math.max(30, Math.min(450, x)), y: startY - (Math.floor(i / 2) * 20) });
        }
        return positions;
      },
      // Line formation (top spread)
      () => {
        const positions = [];
        const spacing = Math.min(60, 400 / Math.max(count - 1, 1)); // Adjust spacing to fit screen
        const startX = Math.max(30, 240 - ((count - 1) * spacing / 2));
        for (let i = 0; i < count; i++) {
          const x = startX + (i * spacing);
          positions.push({ x: Math.min(450, x), y: -30 });
        }
        return positions;
      },
      // Diagonal cascade (top left to right)
      () => {
        const positions = [];
        const startX = 80;
        const spacing = Math.min(50, 370 / Math.max(count - 1, 1));
        for (let i = 0; i < count; i++) {
          positions.push({ x: startX + (i * spacing), y: -30 - (i * 15) });
        }
        return positions;
      },
      // Scattered top formation
      () => {
        const positions = [];
        for (let i = 0; i < count; i++) {
          const x = 60 + (i * (360 / Math.max(count - 1, 1)));
          const yOffset = Math.random() * 40;
          positions.push({ x: Math.min(420, x), y: -30 - yOffset });
        }
        return positions;
      }
    ];

    // Pick random formation pattern
    const pattern = patterns[Math.floor(Math.random() * patterns.length)];
    return pattern();
  }

  private getTotalEnemiesSpawned(): number {
    if (this.useNewWaveSystem) {
      // Count how many enemies have been spawned by looking at remaining counts
      const spawned = this.spawnGroups.reduce((sum, group) => {
        const original = this.currentWaveDef!.spawns.find(s => s.enemyType === group.enemyType);
        return sum + (original ? original.count - group.remainingCount : 0);
      }, 0);
      return spawned;
    }
    return this.enemiesSpawned;
  }

  private spawnEnemies(): void {
    const waveConfig = getWaveConfig(this.currentRegion, this.currentWave);
    if (!waveConfig || waveConfig.enemies.length === 0) return;

    // Spawn 1 enemy per spawn cycle (frequency controlled by currentSpawnDelay)
    const enemiesToSpawn = 1;

    for (let i = 0; i < enemiesToSpawn; i++) {
      // Pick a random enemy type from this wave's config
      const enemyGroup = waveConfig.enemies[Math.floor(Math.random() * waveConfig.enemies.length)];
      
      // Randomized spawn positions like original game
      let finalX, finalY;
      const spawnSide = Math.random();
      
      if (spawnSide < 0.7) {
        // Top spawn (most common - 70%)
        finalX = Math.random() * 450 + 15;
        finalY = -30;
      } else if (spawnSide < 0.85) {
        // Left side spawn (15%)
        finalX = -30;
        finalY = Math.random() * 200;
      } else {
        // Right side spawn (15%)
        finalX = 480 + 30;
        finalY = Math.random() * 200;
      }
      
      const enemy = new Enemy(finalX, finalY, enemyGroup.type);
      this.enemies.push(enemy);
      this.enemiesSpawned++;
    }
  }

  private checkWaveComplete(): void {
    if (this.useNewWaveSystem) {
      // New system: wave complete when duration elapsed and all enemies defeated
      const allSpawned = this.spawnGroups.every(group => group.remainingCount === 0);
      const allDefeated = this.enemies.every(e => !e.active);
      
      if (allSpawned && allDefeated) {
        this.waveComplete = true;
        this.waveActive = false;
        console.log(`Wave ${this.currentWave} complete! (NEW SYSTEM)`);
      }
    } else {
      // Old system: wave complete when spawn limit reached and most enemies defeated
      const allEnemiesDefeated = this.enemies.every(e => !e.active);
      const bossDefeated = !this.boss || !this.boss.active;
      const reachedSpawnLimit = this.enemiesSpawned >= this.maxEnemiesPerWave;
      const minWaveTime = 5;
      
      if ((allEnemiesDefeated && bossDefeated && reachedSpawnLimit) || 
          (reachedSpawnLimit && this.enemiesKilled >= this.maxEnemiesPerWave * 0.8 && this.waveTimer >= minWaveTime)) {
        this.waveComplete = true;
        this.waveActive = false;
        console.log(`Wave ${this.currentWave} complete! (OLD SYSTEM)`);
      }
    }
  }

  public render(ctx: CanvasRenderingContext2D): void {
    // Render all enemies
    for (const enemy of this.enemies) {
      if (enemy.active) {
        enemy.render(ctx);
      }
    }

    // Render boss
    if (this.boss && this.boss.active) {
      this.boss.render(ctx);
    }
  }

  public getEnemies(): Enemy[] {
    return this.enemies.filter(e => e.active);
  }

  public getBoss(): Boss | null {
    return this.boss && this.boss.active ? this.boss : null;
  }

  public isWaveActive(): boolean {
    return this.waveActive;
  }

  public isWaveComplete(): boolean {
    return this.waveComplete;
  }

  public getCurrentRegion(): number {
    return this.currentRegion;
  }

  public getCurrentWave(): number {
    return this.currentWave;
  }

  public getWaveState(): WaveState {
    const waveConfig = getWaveConfig(this.currentRegion, this.currentWave);
    return {
      region: this.currentRegion,
      wave: this.currentWave,
      active: this.waveActive,
      complete: this.waveComplete,
      isBoss: waveConfig?.isBoss || false,
      isMiniBoss: waveConfig?.isMiniBoss || false,
    };
  }

  public reset(): void {
    this.currentRegion = 1;
    this.currentWave = 1;
    this.waveActive = false;
    this.waveComplete = false;
    this.enemies = [];
    this.boss = null;
    this.useNewWaveSystem = false;
    this.spawnGroups = [];
    this.enemiesKilledThisWave = 0;
    this.powerUpDropped = false;
  }
  
  // Spawn a single enemy at specific position (for Greed split behavior)
  public spawnEnemy(type: string, x: number, y: number): void {
    const enemy = new Enemy(x, y, type);
    this.enemies.push(enemy);
    // Don't increment spawn counters for split enemies (they're bonus enemies)
  }
  
  // Get power-up drop info for current wave
  public getPowerUpDrop(): { type: string; ready: boolean } | null {
    if (this.useNewWaveSystem && this.currentWaveDef?.powerUpDrop) {
      return {
        type: this.currentWaveDef.powerUpDrop.type,
        ready: this.powerUpDropped
      };
    }
    return null;
  }
}
